import { PlatformAuthorizer } from '../PlatformAuthorizer';
import { PlatformConfig } from '../PlatformConfig';
import { OAuthService } from '../OAuth2/OAuthService';
import { UserAuthorizationDelegate } from '../UserAuthorization';
import { AuthorizationData } from '../OAuth2/model/AuthorizationData';
import { OAuthServiceImpl } from '../OAuth2/internal/OAuthServiceImpl';
import {
  createKeyValueStore,
  KeyValueStore,
  LocalStorage,
  Error,
  isError,
  createLogger,
  Logger,
  createSafeTimeout,
  TimeoutController,
} from '@quickplay/foundation';

let platformAuthorizer: PlatformAuthorizer | undefined;
const PLATFORM_SERVICE_STORE_NAME = `PLATFORM_AUTHORIZER`;
const PLATFORM_SERVICE_STORE_ACCESS_TOKEN = `PLATFORM_SERVICE_STORE_ACCESS_TOKEN`;
const ACCESS_TOKEN_EXPIRY_GUARD_MSEC = 15000;
const THRESHOLD_PERCENTAGE = 0.05;

// eslint-disable-next-line sonarjs/cognitive-complexity
function createPlatformAuthorizer(
  platformConfig: PlatformConfig,
  userAuthorizationDelegate: UserAuthorizationDelegate,
): PlatformAuthorizer {
  if (platformAuthorizer) {
    return platformAuthorizer;
  }
  const safeTimeout: TimeoutController = createSafeTimeout();
  const logger: Logger = createLogger();
  logger.log(`Starting the Platform service `);
  const oAuthService: OAuthService = OAuthServiceImpl(platformConfig.authorizationEndPoint);
  const authorizeKeyValueStore: KeyValueStore<AuthorizationData> = createKeyValueStore(
    PLATFORM_SERVICE_STORE_NAME,
    sessionStorage as LocalStorage,
  );

  platformAuthorizer = {
    get platformConfig(): PlatformConfig {
      return platformConfig;
    },

    get oAuthService(): OAuthService {
      return oAuthService;
    },

    get userAuthorizationDelegate(): UserAuthorizationDelegate {
      return userAuthorizationDelegate;
    },

    dispose(): void {
      logger.log(`Stopping the Platform service `);
      resetAuthorizationData();
      safeTimeout.clear();
      platformAuthorizer = undefined;
    },

    ensureAuthorization(): Promise<AuthorizationData | Error> {
      return internalEnsureAuthorization();
    },
  };

  async function internalEnsureAuthorization(): Promise<AuthorizationData | Error> {
    const authorizationData: AuthorizationData | undefined = getStoredAuthorizationData();
    if (!authorizationData || !isValidAuthorizationToken(authorizationData)) {
      return generateNewAuthorizationToken();
    }
    return authorizationData;
  }

  function onError(error: Error): Promise<Error> {
    resetAuthorizationData();
    safeTimeout.clear();
    return Promise.reject(error);
  }

  function isValidAuthorizationToken(authorizationData: AuthorizationData): boolean {
    // Token is expired if the elapsedTime is less than the threshold
    const elapsedTimeInSeconds = Math.round(Date.now() / 1000) - authorizationData.createdTime;
    const remainingTimeToExpire = authorizationData.tokenExpiresInSeconds - elapsedTimeInSeconds;
    // Token Expiry Threshold is set to 5% of token expiry time.
    const tokenExpiryThreshold = THRESHOLD_PERCENTAGE * authorizationData.tokenExpiresInSeconds;

    // New token is retrieved if the token has expired and it is about to expire
    if (remainingTimeToExpire <= tokenExpiryThreshold) {
      logger.debug(`Authorization token has expired`);
      return false;
    }
    return true;
  }

  async function generateNewAuthorizationToken(): Promise<AuthorizationData | Error> {
    try {
      const response = await oAuthService.authorize(platformConfig.clientID, platformConfig.clientSecret);
      if (isError(response)) {
        logger.debug(`Authorization request has failed ${response}`);
        return onError(response);
      } else {
        processAuthorizationResponse(response);
        return Promise.resolve(response);
      }
    } catch (error) {
      logger.debug(`Authorization request has failed ${error}`);
      return onError(error as Error);
    }
  }

  function saveAuthorizationData(authorizationData: AuthorizationData): void {
    authorizeKeyValueStore.set(PLATFORM_SERVICE_STORE_ACCESS_TOKEN, authorizationData);
  }

  function getStoredAuthorizationData(): AuthorizationData | undefined {
    return authorizeKeyValueStore.get(PLATFORM_SERVICE_STORE_ACCESS_TOKEN);
  }

  function resetAuthorizationData(): void {
    authorizeKeyValueStore.remove(PLATFORM_SERVICE_STORE_ACCESS_TOKEN);
  }

  function scheduleAuthTokenRefresh(refreshIntervalMillis: number): void {
    safeTimeout.set(async () => {
      try {
        const authorizationData = getStoredAuthorizationData();
        if (!authorizationData) {
          throw authorizationData;
        }

        await generateNewAuthorizationToken();
      } catch (error) {
        logger.debug(`Refresh Authorization request has failed ${error}`);
        onError(error as Error).catch(() => {
          // rejection is handled — error already logged above
        });
      }
    }, refreshIntervalMillis);
  }
  function processAuthorizationResponse(response: AuthorizationData): void {
    try {
      saveAuthorizationData(response);
      scheduleAuthTokenRefresh(response.tokenExpiresInSeconds * 1000 - ACCESS_TOKEN_EXPIRY_GUARD_MSEC);
    } catch (error) {
      throw onError(error as Error);
    }
  }

  return platformAuthorizer;
}
export default createPlatformAuthorizer;
